<template>
  <div class="season-phase-badge-wrapper" v-if="!loading && currentPhase">
    <div
      class="season-phase-badge"
      :style="{
        backgroundColor: phaseInfo.bgColor,
        borderColor: phaseInfo.borderColor,
        color: phaseInfo.color,
      }"
      :title="phaseInfo.description"
      @click="toggleExpanded"
    >
      <span class="phase-emoji">{{ phaseInfo.emoji }}</span>
      <span class="phase-label">{{ phaseInfo.shortLabel }}</span>
      <span v-if="isOverride" class="override-dot" title="Admin Override Active"></span>
    </div>

    <!-- Expanded info panel -->
    <transition name="fade">
      <div v-if="expanded" class="phase-info-panel" @click.stop>
        <div class="panel-header">
          <span class="phase-full-label" :style="{ color: phaseInfo.color }">
            {{ phaseInfo.emoji }} {{ phaseInfo.label }}
          </span>
          <button class="close-btn" @click="expanded = false">&times;</button>
        </div>

        <p class="phase-description">{{ phaseInfo.description }}</p>

        <!-- Next Activity Context (Training or Match) -->
        <div v-if="nextActivity" class="activity-context" :class="activityTypeClass">
          <div class="activity-header">
            <span class="activity-icon">{{ nextActivity.type === 'training' ? '🏃' : '⚽' }}</span>
            <span class="activity-label">{{ nextActivity.label }}</span>
          </div>
          <div class="activity-info">
            <span v-if="nextActivity.type === 'match'" class="activity-opponent">
              vs {{ nextActivity.opposition }}
            </span>
            <span class="activity-date">{{ formatMatchDate(nextActivity.date) }}</span>
            <span v-if="nextActivity.time" class="activity-time">{{ nextActivity.time }}</span>
          </div>
          <!-- Nutrition advice for this activity -->
          <div v-if="nextActivity.nutrition_focus" class="activity-nutrition">
            <span class="nutrition-short">{{ nextActivity.nutrition_focus.short }}</span>
            <span class="nutrition-carbs">Carbs: {{ nextActivity.nutrition_focus.carb_target }}g/kg</span>
          </div>
        </div>

        <!-- Show upcoming match if next activity is training -->
        <div v-if="nextActivity && nextActivity.type === 'training' && upcomingMatch" class="upcoming-match">
          <div class="upcoming-header">
            <span class="upcoming-icon">⚽</span>
            <span class="upcoming-label">Next Match</span>
          </div>
          <div class="upcoming-info">
            <span class="upcoming-opponent">vs {{ upcomingMatch.opposition }}</span>
            <span class="upcoming-date">{{ upcomingMatch.label }} - {{ formatMatchDate(upcomingMatch.date) }}</span>
          </div>
        </div>

        <!-- Detailed Nutrition Focus -->
        <div v-if="detailedNutritionFocus" class="nutrition-focus">
          <span class="focus-label">Nutrition Focus:</span>
          <span class="focus-text">{{ detailedNutritionFocus }}</span>
        </div>

        <!-- Override Info -->
        <div v-if="isOverride" class="override-info">
          <span class="override-label">Override Active</span>
          <span v-if="overrideInfo.reason" class="override-reason">
            {{ overrideInfo.reason }}
          </span>
          <span v-if="overrideInfo.expires" class="override-expires">
            Expires: {{ formatDate(overrideInfo.expires) }}
          </span>
        </div>

        <!-- Quick Link to Settings -->
        <router-link
          v-if="isAdmin"
          :to="{ name: 'Settings' }"
          class="settings-link"
          @click="expanded = false"
        >
          Configure Season Phases
        </router-link>
      </div>
    </transition>

    <!-- Backdrop to close panel -->
    <div v-if="expanded" class="backdrop" @click="expanded = false"></div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { SeasonPhases } from "@/services/season-phases-service";

export default {
  name: "SeasonPhaseBadge",

  data() {
    return {
      loading: true,
      expanded: false,
      currentPhase: null,
      phaseData: null,
      nutritionProfile: null,
      matchContext: null,
      activityData: null, // Stores getNextActivity response
    };
  },

  computed: {
    ...mapGetters(["getProfile"]),

    clubId() {
      return this.getProfile?.clubId;
    },

    teamId() {
      // Get the first team ID if available
      return this.getProfile?.teamId || null;
    },

    isAdmin() {
      return this.getProfile?.userType === "Admin";
    },

    isOverride() {
      return this.phaseData?.is_override || false;
    },

    overrideInfo() {
      if (!this.isOverride) return null;
      return {
        expires: this.phaseData?.expires,
        reason: this.phaseData?.reason,
      };
    },

    phaseInfo() {
      return SeasonPhases.getPhaseDisplayInfo(this.currentPhase || "regular");
    },

    matchDayClass() {
      if (!this.matchContext) return "";
      const days = this.matchContext.days_to_match;
      if (days === 0) return "match-day";
      if (days === 1) return "match-day-minus-1";
      if (days < 0) return "recovery-day";
      return "training-day";
    },

    nutritionFocus() {
      // Get nutrition focus based on match day or phase
      if (this.matchContext) {
        const days = this.matchContext.days_to_match;
        if (days === 0) return "Match fuel: Quick carbs pre/at halftime";
        if (days === 1) return "Carb loading day: 6-8g/kg";
        if (days === -1) return "Recovery: Replenish glycogen + protein";
        if (days === 2) return "Preparation: Increase carb intake";
      }
      return this.nutritionProfile?.primary_goal || this.phaseInfo.focus;
    },

    // Smart context: next activity (training or match)
    nextActivity() {
      return this.activityData?.next_activity || null;
    },

    // If next activity is training, show upcoming match too
    upcomingMatch() {
      if (this.nextActivity?.type === 'training' && this.activityData?.next_match) {
        return this.activityData.next_match;
      }
      return null;
    },

    // CSS class based on activity type
    activityTypeClass() {
      if (!this.nextActivity) return '';
      return this.nextActivity.type === 'training' ? 'activity-training' : 'activity-match';
    },

    // Detailed nutrition focus for the expanded panel
    detailedNutritionFocus() {
      if (this.nextActivity?.nutrition_focus?.detail) {
        return this.nextActivity.nutrition_focus.detail;
      }
      return this.nutritionProfile?.description || this.phaseInfo.focus;
    },
  },

  watch: {
    clubId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadPhaseData();
        }
      },
    },
  },

  methods: {
    async loadPhaseData() {
      if (!this.clubId) {
        console.log("[SeasonPhaseBadge] No clubId available yet");
        this.loading = false;
        return;
      }

      console.log("[SeasonPhaseBadge] Loading phase data for club:", this.clubId);
      this.loading = true;

      try {
        // Load phase and nutrition context in parallel
        const [phaseResponse, nutritionResponse] = await Promise.all([
          SeasonPhases.getCurrentPhase(this.clubId, this.teamId),
          SeasonPhases.getNutritionProfile(this.clubId, this.teamId),
        ]);

        console.log("[SeasonPhaseBadge] Phase response:", phaseResponse.data);

        if (phaseResponse.data.success) {
          this.phaseData = phaseResponse.data.data;
          this.currentPhase = this.phaseData.phase;
        } else {
          // Fallback to calculated default if API fails
          console.warn("[SeasonPhaseBadge] API returned error, using default phase");
          this.currentPhase = this.calculateDefaultPhase();
          this.phaseData = { phase: this.currentPhase, source: 'fallback', is_override: false };
        }

        if (nutritionResponse.data.success) {
          this.nutritionProfile = nutritionResponse.data.data.nutrition;
        }

        // Load activity context (training or match) if we have a team
        if (this.teamId) {
          const activityResponse = await SeasonPhases.getNextActivity(this.teamId);
          if (activityResponse.data.success && activityResponse.data.data) {
            this.activityData = activityResponse.data.data;
            // Also set matchContext for backward compatibility
            if (activityResponse.data.data.next_match) {
              this.matchContext = {
                days_to_match: activityResponse.data.data.next_match.days_diff,
                opposition: activityResponse.data.data.next_match.opposition,
                date: activityResponse.data.data.next_match.date
              };
            }
          }
        }
      } catch (error) {
        console.error("[SeasonPhaseBadge] Failed to load season phase data:", error);
        // Fallback to calculated default on error
        this.currentPhase = this.calculateDefaultPhase();
        this.phaseData = { phase: this.currentPhase, source: 'fallback', is_override: false };
      } finally {
        this.loading = false;
      }
    },

    calculateDefaultPhase() {
      // Calculate phase based on current month (defaults)
      const month = new Date().getMonth() + 1; // 1-12
      if (month >= 6 && month <= 7) return 'preseason';
      if (month >= 8 || month <= 5) return 'regular';
      return 'offseason';
    },

    toggleExpanded() {
      this.expanded = !this.expanded;
    },

    formatDate(date) {
      return SeasonPhases.formatDateForDisplay(date);
    },

    formatMatchDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const options = { weekday: "short", day: "numeric", month: "short" };
      return date.toLocaleDateString("en-GB", options);
    },
  },
};
</script>

<style scoped lang="scss">
.season-phase-badge-wrapper {
  position: relative;
}

.season-phase-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 16px;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

.phase-emoji {
  font-size: 1em;
}

.phase-label {
  font-size: 0.75rem;
}

.override-dot {
  width: 6px;
  height: 6px;
  background: #dc3545;
  border-radius: 50%;
  margin-left: 2px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Expanded Panel */
.phase-info-panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 280px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 16px;
  z-index: 1000;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.phase-full-label {
  font-weight: 600;
  font-size: 1rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #333;
  }
}

.phase-description {
  color: #6c757d;
  font-size: 0.85rem;
  margin: 0 0 12px 0;
}

/* Activity Context (Training or Match) */
.activity-context {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 12px;
  border-left: 3px solid #6c757d;

  &.activity-training {
    background: #e8f5e9;
    border-left-color: #28a745;
  }

  &.activity-match {
    background: #fff3e0;
    border-left-color: #fd7e14;
  }
}

.activity-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.activity-icon {
  font-size: 1.1em;
}

.activity-label {
  font-weight: 600;
  font-size: 0.85rem;
  color: #333;
}

.activity-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.8rem;
  color: #555;
}

.activity-opponent {
  font-weight: 600;
  color: #333;
}

.activity-date {
  color: #666;
}

.activity-time {
  color: #888;
}

.activity-nutrition {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed #ddd;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.75rem;
}

.nutrition-short {
  color: #2e7d32;
  font-weight: 500;
}

.nutrition-carbs {
  color: #666;
}

/* Upcoming Match (shown when next activity is training) */
.upcoming-match {
  background: #f0f4ff;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 12px;
  border-left: 3px solid #007bff;
}

.upcoming-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}

.upcoming-icon {
  font-size: 0.9em;
}

.upcoming-label {
  font-weight: 600;
  font-size: 0.75rem;
  color: #007bff;
}

.upcoming-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 0.75rem;
}

.upcoming-opponent {
  font-weight: 600;
  color: #333;
}

.upcoming-date {
  color: #666;
}

/* Match Context */
.match-context {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 12px;
}

.match-day-label {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 6px;

  &.match-day {
    background: #dc3545;
    color: white;
  }

  &.match-day-minus-1 {
    background: #fd7e14;
    color: white;
  }

  &.recovery-day {
    background: #28a745;
    color: white;
  }

  &.training-day {
    background: #6c757d;
    color: white;
  }
}

.match-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.match-opponent {
  font-weight: 600;
  font-size: 0.9rem;
}

.match-date {
  font-size: 0.8rem;
  color: #6c757d;
}

/* Nutrition Focus */
.nutrition-focus {
  background: #e8f5e9;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 12px;
  font-size: 0.8rem;
}

.focus-label {
  font-weight: 600;
  color: #2e7d32;
  display: block;
  margin-bottom: 2px;
}

.focus-text {
  color: #1b5e20;
}

/* Override Info */
.override-info {
  background: #fff3cd;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 12px;
  font-size: 0.8rem;
}

.override-label {
  font-weight: 600;
  color: #856404;
  display: block;
  margin-bottom: 2px;
}

.override-reason,
.override-expires {
  display: block;
  color: #856404;
  font-size: 0.75rem;
}

/* Settings Link */
.settings-link {
  display: block;
  text-align: center;
  color: #007bff;
  font-size: 0.8rem;
  text-decoration: none;
  padding: 8px;
  border-top: 1px solid #eee;
  margin: 0 -16px -16px -16px;
  border-radius: 0 0 8px 8px;

  &:hover {
    background: #f8f9fa;
    text-decoration: none;
  }
}

/* Backdrop */
.backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Tablet adjustments */
@media screen and (max-width: 768px) {
  .season-phase-badge {
    padding: 3px 8px;
    font-size: 0.75rem;
  }

  .phase-label {
    display: none;
  }

  .phase-emoji {
    font-size: 1.1em;
  }

  .phase-info-panel {
    width: 280px;
    right: -20px;
  }
}

/* Mobile - Bottom Sheet Pattern */
@media screen and (max-width: 480px) {
  .season-phase-badge {
    padding: 4px 8px;
  }

  .phase-emoji {
    font-size: 1.2em;
  }

  /* Full-width bottom sheet on mobile */
  .phase-info-panel {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-height: 70vh;
    overflow-y: auto;
    border-radius: 16px 16px 0 0;
    padding: 20px;
    padding-bottom: calc(20px + env(safe-area-inset-bottom));
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.2);
    animation: slideUp 0.3s ease;
  }

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  .panel-header {
    margin-bottom: 16px;
  }

  .phase-full-label {
    font-size: 1.1rem;
  }

  .close-btn {
    font-size: 1.5rem;
    padding: 8px;
    margin: -8px;
  }

  .match-context {
    padding: 12px;
  }

  .match-day-label {
    font-size: 0.85rem;
    padding: 4px 10px;
  }

  .match-opponent {
    font-size: 1rem;
  }

  .nutrition-focus {
    padding: 12px;
  }

  /* Activity context mobile styles */
  .activity-context {
    padding: 12px;
  }

  .activity-label {
    font-size: 0.9rem;
  }

  .activity-info {
    font-size: 0.85rem;
  }

  .activity-nutrition {
    font-size: 0.8rem;
  }

  .upcoming-match {
    padding: 10px 12px;
  }

  .upcoming-label {
    font-size: 0.8rem;
  }

  .upcoming-info {
    font-size: 0.8rem;
  }

  .settings-link {
    padding: 14px;
    font-size: 0.9rem;
    margin: 0 -20px -20px -20px;
    margin-bottom: calc(-20px - env(safe-area-inset-bottom));
  }

  /* Darker backdrop on mobile */
  .backdrop {
    background: rgba(0, 0, 0, 0.5);
  }
}
</style>
